import React, { useEffect, useState } from 'react';
import { DillDropdown } from '../../../general/Dill/DillDropdown';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getSupplierPaymentTerms } from '../../../../redux/services/suppliersService';

// Eastern's default terms once the credit team approves.
const DEFAULT_TERMS = 'NET 30';

// The Eclipse part of approving an internal / external form (Eclipse suppliers; the backend side
// is eclipseFormCustomerService.ts): the Bill-To is updated (Reactivate) or only the parent of a
// new Ship-To; the Ship-To is updated (Reactivate), created under the Bill-To (New Ship-To, Job
// Information Sheet) or left alone. The credit team picks the accounts here — they aren't on the
// form. Terms default to Net 30 on every account touched. Kept apart from the P21 inputs in
// ApproveInternalFormModal.
const EclipseFormApprovalFields = ({
  open,
  billToAction,
  shipToAction,
  billToCustomerId,
  billToError,
  onBillToChange,
  shipToAccountNumber,
  shipToError,
  onShipToChange,
  terms,
  onTermsChange,
}: {
  open: boolean;
  billToAction: 'UPDATE' | 'NONE';
  shipToAction: 'UPDATE' | 'CREATE' | 'NONE';
  billToCustomerId: string;
  billToError: string;
  onBillToChange: (value: string) => void;
  shipToAccountNumber: string;
  shipToError: string;
  onShipToChange: (value: string) => void;
  terms: string;
  onTermsChange: (value: string) => void;
}) => {
  const dispatch = useAppDispatch();
  const supplierId = useAppSelector((state) => state.auth.currentSupplier?.id);
  // The supplier's terms list (names; the backend sends each one's Eclipse code).
  const [termOptions, setTermOptions] = useState<string[]>([]);

  useEffect(() => {
    if (!open || !supplierId) return;
    dispatch(getSupplierPaymentTerms({ supplierId })).then((result) => {
      const names = (((result.payload as any)?.data ?? []) as { name?: string }[])
        .map((term) => term.name ?? '')
        .filter(Boolean);
      setTermOptions(Array.from(new Set(names)));
    });
  }, [open, supplierId, dispatch]);

  // Net 30 preselected — the supplier's own entry for it when it's in their terms list.
  const hasDefaultTerms = termOptions.some((name) => name.toUpperCase() === DEFAULT_TERMS);
  useEffect(() => {
    if (!open || terms) return;
    onTermsChange(
      termOptions.find((name) => name.toUpperCase() === DEFAULT_TERMS) ?? DEFAULT_TERMS
    );
  }, [open, terms, termOptions, onTermsChange]);

  return (
    <>
      <p className="text-sm GREY_600-CLR">
        {[
          billToAction === 'UPDATE'
            ? 'Approving updates the Bill-To with the form’s Bill-To details and the terms below.'
            : '',
          shipToAction === 'CREATE'
            ? `Approving creates a new Ship-To in Eclipse under this Bill-To, from this form’s name, address, phone, branch, sales rep and exemptions, with ${
                billToAction === 'UPDATE' ? 'the terms below' : 'the Bill-To’s terms'
              } and the credit limit.`
            : shipToAction === 'UPDATE'
            ? 'Approving updates the Ship-To with the form’s Ship-To details, the terms below and the credit limit.'
            : '',
        ]
          .filter(Boolean)
          .join(' ')}
      </p>
      <AppInputField
        id="approveEclipseBillToCustomerId"
        label="Bill-To Customer Number in Eclipse"
        isRequired
        value={billToCustomerId}
        errorText={billToError}
        onTextChange={(text: string) => onBillToChange(text)}
      />
      {shipToAction === 'UPDATE' && (
        <AppInputField
          id="approveEclipseShipToAccountNumber"
          label="Ship-To Number in Eclipse"
          isRequired
          value={shipToAccountNumber}
          errorText={shipToError}
          onTextChange={(text: string) => onShipToChange(text)}
        />
      )}
      {/* A new Ship-To under a Bill-To that's left alone takes the Bill-To's terms. */}
      {(billToAction === 'UPDATE' || shipToAction === 'UPDATE') && (
        <DillDropdown
          containerClassName="h-[40px] p-1"
          label={
            billToAction === 'UPDATE' && shipToAction !== 'NONE'
              ? 'Bill-To and Ship-To Terms'
              : billToAction === 'UPDATE'
              ? 'Bill-To Terms'
              : 'Ship-To Terms'
          }
          placeholder="Select terms"
          value={terms || null}
          items={(hasDefaultTerms ? termOptions : [DEFAULT_TERMS, ...termOptions]).map((name) => ({
            value: name,
            label: name,
          }))}
          onSelectChange={(item) => onTermsChange(item.value)}
        />
      )}
    </>
  );
};

export default EclipseFormApprovalFields;
